import { useState } from 'react';
import { theme } from '@frontend/theme';
import { Modal, useModalControl } from '@frontend/design-system';
import type { SendingMediaItem } from '../types';
import { getAssetKind } from '../utils/file-utils';
import { PreviewFile } from './preview-file';
import { PreviewImage } from './preview-image';

type MediaUploadPreviewProps = {
  media: SendingMediaItem[];
  removeMediaItem: (mediaId: string) => void;
  inPopout?: boolean;
  removeImageTrackingId?: string;
  showScrollBar?: boolean;
};

export const MediaUploadPreview = ({
  media,
  removeMediaItem,
  inPopout,
  removeImageTrackingId,
  showScrollBar,
}: MediaUploadPreviewProps) => {
  const [selectedImage, setSelectedImage] = useState<string>();

  const previewModalControls = useModalControl();

  return (
    <ul
      css={[
        {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'start',
          height: media.length ? (inPopout ? theme.spacing(16) : '20vh') : '0',
          width: '100%',
          padding: media.length ? theme.spacing(2) : 0,
          borderTop: media.length ? (inPopout ? 'none' : `1px solid ${theme.colors.neutral20}`) : 'none',
          gap: theme.spacing(2),
          overflowX: 'auto',
        },
        !media.length && {
          transition: 'height 300ms ease-in-out, border-top 300ms ease-in-out, padding 300ms ease-in-out',
        },
        inPopout && {
          backgroundColor: theme.colors.neutral5,
          zIndex: theme.zIndex.higher,
        },
        showScrollBar && {
          scrollbarWidth: 'auto',
        },
      ]}
    >
      {!!media.length &&
        media.map((mediaItem) => {
          const assetKind = getAssetKind(mediaItem?.file?.type);
          switch (assetKind) {
            case 'image': {
              return (
                <PreviewImage
                  key={mediaItem.id}
                  image={mediaItem}
                  onDelete={() => {
                    removeMediaItem(mediaItem.id);
                  }}
                  onPreview={() => {
                    setSelectedImage(mediaItem.mediaObj?.url);
                    previewModalControls.openModal();
                  }}
                  inPopout={inPopout}
                  removeImageTrackingId={removeImageTrackingId}
                />
              );
            }
            // TODO: implement video preview
            default: {
              return (
                <PreviewFile
                  key={mediaItem.id}
                  media={mediaItem}
                  onDelete={() => {
                    removeMediaItem(mediaItem.id);
                  }}
                  removeImageTrackingId={removeImageTrackingId}
                  assetKind={assetKind}
                />
              );
            }
          }
        })}
      {selectedImage && (
        <Modal
          {...previewModalControls.modalProps}
          maxWidth={0}
          css={{
            padding: 0,
            borderRadius: theme.borderRadius.medium,
            overflow: 'hidden',
          }}
        >
          <img src={selectedImage} css={{ maxHeight: '70vh', maxWidth: '50vw', padding: 0 }} />
        </Modal>
      )}
    </ul>
  );
};
